import { effect, inject, Injectable, type Signal, signal } from '@angular/core';

import type { LeapBotConversation } from '../../interfaces/leapbot.interface';
import { AuthService } from '../auth/auth.service';

/** Storage key prefix; the `v1` suffix lets a future shape change ignore old entries. */
const STORAGE_PREFIX = 'fury.leapbot.conversations.v1:';
const ANONYMOUS_SUFFIX = 'anonymous';

/** Most conversations kept per user; the oldest is dropped first. */
export const MAX_CONVERSATIONS = 50;
/** Most messages kept per conversation; the oldest is dropped first. */
export const MAX_MESSAGES_PER_CONVERSATION = 200;

/**
 * Browser-only history of LeapBot conversations, separated per signed-in user (`sub`).
 *
 * Every storage access is guarded: when `localStorage` is blocked or full the chat keeps working
 * and the history for the session stays in memory only.
 */
@Injectable({ providedIn: 'root' })
export class LeapBotHistoryService {
  private readonly auth = inject(AuthService);
  private readonly conversationsState = signal<LeapBotConversation[]>([]);

  /** Conversations of the current user, most recent first. */
  readonly conversations: Signal<LeapBotConversation[]> = this.conversationsState.asReadonly();

  constructor() {
    // Reload whenever another user signs in so conversations never leak between accounts.
    effect(() => {
      this.conversationsState.set(this.readFromStorage(this.storageKey(this.auth.userId())));
    });
  }

  /** Returns a conversation of the current user, or `null` when it does not exist. */
  get(id: string): LeapBotConversation | null {
    return this.conversationsState().find((c) => c.id === id) ?? null;
  }

  /**
   * Creates or replaces a conversation, enforcing the per-conversation and total limits and
   * persisting the result.
   *
   * @param conversation - Conversation to store; messages beyond the cap are trimmed from the start.
   */
  upsert(conversation: LeapBotConversation): void {
    const trimmed: LeapBotConversation = {
      ...conversation,
      messages: conversation.messages.slice(-MAX_MESSAGES_PER_CONVERSATION),
    };
    const others = this.conversationsState().filter((c) => c.id !== trimmed.id);
    const next = [trimmed, ...others]
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      .slice(0, MAX_CONVERSATIONS);
    this.commit(next);
  }

  /** Deletes one conversation of the current user. */
  remove(id: string): void {
    this.commit(this.conversationsState().filter((c) => c.id !== id));
  }

  /** Deletes every conversation of the current user. */
  clear(): void {
    this.commit([]);
  }

  private commit(next: LeapBotConversation[]): void {
    const key = this.storageKey(this.auth.userId());
    let kept = next;
    // On a full storage drop the oldest conversations until the write fits.
    while (!this.writeToStorage(key, kept) && kept.length > 1 && this.isQuotaFailure) {
      kept = kept.slice(0, -1);
    }
    this.conversationsState.set(kept);
  }

  private isQuotaFailure = false;

  private storageKey(userId: string | null): string {
    return `${STORAGE_PREFIX}${userId ?? ANONYMOUS_SUFFIX}`;
  }

  private readFromStorage(key: string): LeapBotConversation[] {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as LeapBotConversation[]) : []; // shape written by this service under a versioned key
    } catch {
      return [];
    }
  }

  private writeToStorage(key: string, conversations: LeapBotConversation[]): boolean {
    try {
      localStorage.setItem(key, JSON.stringify(conversations));
      this.isQuotaFailure = false;
      return true;
    } catch (error) {
      this.isQuotaFailure = error instanceof DOMException && error.name === 'QuotaExceededError';
      return false;
    }
  }
}
